
import React from "react";

interface PdfSettings {
  company_name: string;
  header_text: string;
  footer_text: string;
  header_image_url: string;
  font_family?: string;
  font_size_body?: number;
  font_size_footnote?: number;
  margin_top?: number;
  margin_bottom?: number;
  margin_left?: number;
  margin_right?: number;
  line_spacing?: number;
  paragraph_indent?: number;
  page_numbering_enabled?: boolean;
  page_numbering_position?: string;
  lawyer_name?: string;
  oab_number?: string;
  oab_state?: string;
  include_lawyer_signature?: boolean;
}

interface PdfPreviewProps {
  settings: PdfSettings;
}

export const PdfPreview: React.FC<PdfPreviewProps> = ({ settings }) => {
  const marginTop = settings.margin_top || 30;
  const marginBottom = settings.margin_bottom || 20;
  const marginLeft = settings.margin_left || 30;
  const marginRight = settings.margin_right || 20;
  const fontSize = settings.font_size_body || 12;
  const fontFamily = settings.font_family || "Times New Roman";
  const lineHeight = settings.line_spacing || 1.5;

  return (
    <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
      {/* Preview Container */}
      <div 
        className="w-full aspect-[210/297] bg-white relative text-black"
        style={{
          fontFamily: fontFamily === "Times New Roman" ? "serif" : "sans-serif",
          fontSize: `${Math.max(fontSize * 0.6, 8)}px`,
          lineHeight: lineHeight,
          padding: `${marginTop * 0.8}px ${marginRight * 0.8}px ${marginBottom * 0.8}px ${marginLeft * 0.8}px`
        }}
      >
        {/* Header */}
        <div className="border-b border-gray-200 pb-3 mb-4 min-h-[60px] flex items-center">
          <div className="flex items-center gap-3 w-full">
            {settings.header_image_url && (
              <div className="flex-shrink-0">
                <img 
                  src={settings.header_image_url} 
                  alt="Logo" 
                  className="max-h-10 w-auto"
                />
              </div>
            )}
            <div className="flex-1">
              {settings.company_name && (
                <h2 className="font-bold text-gray-900 mb-1" style={{ fontSize: `${fontSize * 0.8}px` }}>
                  {settings.company_name}
                </h2>
              )}
              {settings.header_text && (
                <p className="text-gray-600 leading-tight" style={{ fontSize: `${(settings.font_size_footnote || 10) * 0.7}px` }}>
                  {settings.header_text}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Page Number */}
        {settings.page_numbering_enabled && (
          <div 
            className={`absolute text-gray-600 ${settings.page_numbering_position === 'centro-inferior' ? 'bottom-4 left-1/2 transform -translate-x-1/2' : 'top-4 right-4'}`}
            style={{ fontSize: `${(settings.font_size_footnote || 10) * 0.6}px` }}
          >
            1
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 space-y-3">
          <div className="text-center mb-4">
            <h1 className="font-bold text-gray-900 mb-1" style={{ fontSize: `${fontSize * 1.2}px` }}>
              Justiçai - Assistente Jurídica
            </h1>
            <p className="text-gray-500" style={{ fontSize: `${(settings.font_size_footnote || 10) * 0.7}px` }}>
              Gerado em {new Date().toLocaleString('pt-BR')}
            </p>
          </div>
          
          <div className="space-y-3 text-justify">
            <div className="bg-gray-50 p-2 rounded border">
              <p 
                className="text-gray-700 leading-relaxed"
                style={{ 
                  textIndent: `${(settings.paragraph_indent || 20) * 0.8}px`,
                  lineHeight: lineHeight 
                }}
              >
                Este é um exemplo de como ficará o conteúdo das suas conversas no PDF gerado. 
                O texto será formatado conforme as normas ABNT NBR 14724 para documentos jurídicos, 
                mantendo a estrutura original com parágrafos, <strong>textos em negrito</strong> e <em>itálico</em> quando necessário.
              </p>
            </div>
            
            <div className="bg-gray-50 p-2 rounded border">
              <h3 className="font-semibold text-gray-900 mb-2" style={{ fontSize: `${fontSize * 0.9}px` }}>
                Art. 1º - Exemplo de Resposta Jurídica
              </h3>
              <p 
                className="text-gray-700 leading-relaxed"
                style={{ 
                  textIndent: `${(settings.paragraph_indent || 20) * 0.8}px`,
                  lineHeight: lineHeight 
                }}
              >
                § 1º Com base na consulta realizada, posso afirmar que o caso apresenta 
                características específicas que devem ser analisadas sob a ótica da 
                legislação vigente, considerando:
              </p>
              <div className="ml-4 mt-2 space-y-1 text-gray-700">
                <div>I - Os fundamentos legais aplicáveis;</div>
                <div>II - A jurisprudência consolidada;</div>
                <div>III - As peculiaridades do caso concreto.</div>
              </div>
            </div>
            
            <div className="bg-blue-50 p-2 rounded border border-blue-200">
              <p className="text-blue-800" style={{ fontSize: `${(settings.font_size_footnote || 10) * 0.8}px` }}>
                💡 <strong>Dica:</strong> Todas as formatações markdown da conversa 
                serão preservadas no PDF final, mantendo a legibilidade e estrutura conforme ABNT.
              </p>
            </div>
          </div>
        </div>

        {/* Lawyer Signature */}
        {settings.include_lawyer_signature && (settings.lawyer_name || settings.oab_number || settings.oab_state) && (
          <div className="mt-6 pt-4 text-center">
            <div className="border-t border-gray-400 w-48 mx-auto mb-2"></div>
            {settings.lawyer_name && (
              <div className="font-medium text-gray-900" style={{ fontSize: `${fontSize * 0.8}px` }}>
                {settings.lawyer_name}
              </div>
            )}
            {(settings.oab_number || settings.oab_state) && (
              <div className="text-gray-700" style={{ fontSize: `${(settings.font_size_footnote || 10) * 0.8}px` }}>
                OAB/{settings.oab_state || "XX"} {settings.oab_number || "XXXXXX"}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 p-2 bg-gray-50">
          {settings.footer_text ? (
            <p 
              className="text-gray-600 text-center leading-tight"
              style={{ fontSize: `${(settings.font_size_footnote || 10) * 0.6}px` }}
            >
              {settings.footer_text}
            </p>
          ) : (
            <p 
              className="text-gray-400 text-center italic"
              style={{ fontSize: `${(settings.font_size_footnote || 10) * 0.6}px` }}
            >
              Configure o texto do rodapé para aparecer aqui
            </p>
          )}
        </div>
      </div>
      
      {/* Preview Label */}
      <div className="bg-muted px-3 py-2 text-center">
        <p className="text-xs text-muted-foreground">
          Preview - Formato A4 | Fonte: {fontFamily} {fontSize}pt | Margens ABNT: {marginTop}/{marginBottom}/{marginLeft}/{marginRight}mm
        </p>
      </div>
    </div>
  );
};
